import { useState, type ReactNode } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type components } from "@njbsoft/types/customer";
import { ConfirmDialog } from "@/components/confirm-dialog";
import {
  canCancelRequest,
  REQUEST_STATUS_LABEL_KEY,
  REQUEST_STATUS_TONE,
} from "@/components/my-requests/request-status";
import { ThreadPane } from "@/components/my-requests/thread-pane";
import {
  Button,
  buttonClasses,
  EmptyValue,
  LoadError,
  PageTitle,
  Skeleton,
  StatusChip,
} from "@/components/ui";
import { useActiveMembership, useMyCustomerContext } from "@/hooks/use-customer-context";
import { useCustomerInstallation } from "@/hooks/use-customer-devices";
import { useCustomerFacility } from "@/hooks/use-customer-premises";
import {
  cancelRequestErrorKey,
  useCancelTestRequest,
  useCustomerTestRequest,
} from "@/hooks/use-customer-test-requests";
import { formatLongInstantDay } from "@/lib/dates";
import { requestsLink, submissionLink } from "@/lib/destinations";
import { present } from "@/lib/present";
import { requestDeviceLabel } from "@/lib/request-list";
import { uuidParam } from "@/lib/search-params";

type CustomerTestRequest = components["schemas"]["CustomerTestRequest"];
type CustomerInstallation = components["schemas"]["CustomerInstallation"];
type CustomerFacility = components["schemas"]["CustomerFacility"];

export const Route = createFileRoute("/_customer/testing/requests/$requestId")({
  component: RequestRecordPage,
});

const PAGE = "flex flex-col gap-5 lg:gap-6";
const GRID = "grid items-start gap-4.5 lg:grid-cols-[1fr_340px] lg:gap-6";
const TERM = "border-b border-line py-2.25 pr-5.5 text-sm text-muted";
const VALUE = "border-b border-line py-2.25 text-sm font-medium text-ink";

// The loaded page's shape in static blocks: the title, then the record and the
// conversation.
const LOADING = (
  <div className={PAGE}>
    <div>
      <Skeleton height={34} width="55%" />
      <Skeleton height={18} width="30%" className="mt-2" />
    </div>
    <div className={GRID}>
      <Skeleton variant="card" height={220} className="lg:col-start-2 lg:row-start-1" />
      <Skeleton variant="card" height={320} className="lg:col-start-1 lg:row-start-1" />
    </div>
  </div>
);

/** The server's typed refusal code, which the cancel hook adds to its rejection. */
function refusalCode(error: unknown): string | undefined {
  return typeof error === "object" &&
    error !== null &&
    "code" in error &&
    typeof error.code === "string"
    ? error.code
    : undefined;
}

/**
 * One test request: where it stands, when it was asked for, who has it, its
 * report once accepted, the cancel while it is still allowed, and its
 * conversation. An id that cannot be a request is not found without a read.
 */
function RequestRecordPage() {
  const { t } = useTranslation("customer");
  const { requestId } = Route.useParams();
  const id = uuidParam(requestId) ?? "";
  const read = useCustomerTestRequest(id);
  const request = read.data;
  const installationRead = useCustomerInstallation(request?.device_installation_id ?? "");
  const facilityRead = useCustomerFacility(request?.facility_id ?? "");

  if (!id || read.error?.status === 404) {
    return (
      <div
        role="alert"
        className="rounded-card border border-line bg-surface p-4 shadow-card lg:p-5"
      >
        <h1 className="text-lg font-semibold tracking-[-0.01em] text-ink">
          {t("requestDetail.notFound")}
        </h1>
        <Link {...requestsLink()} className={`mt-3 ${buttonClasses("secondary")}`}>
          {t("nav.tabs.myRequests")}
        </Link>
      </div>
    );
  }

  if (read.isError) {
    return (
      <LoadError
        asPage
        title={t("requestDetail.error")}
        body={t("loadError.body")}
        onRetry={() => void read.refetch()}
      />
    );
  }

  // The device and property reads only name the page: a device or property
  // that cannot be read falls back to the request's own serial and no line.
  if (!request || installationRead.isLoading || facilityRead.isLoading) return LOADING;

  return (
    <RequestRecord
      request={request}
      installation={installationRead.data}
      facility={facilityRead.data}
      onReread={() => void read.refetch()}
    />
  );
}

interface RequestRecordProps {
  request: CustomerTestRequest;
  installation: CustomerInstallation | undefined;
  facility: CustomerFacility | undefined;
  onReread: () => void;
}

function RequestRecord({ request, installation, facility, onReread }: RequestRecordProps) {
  const { t, i18n } = useTranslation("customer");
  const active = useActiveMembership();
  const { data: context } = useMyCustomerContext();
  const cancel = useCancelTestRequest();
  const [confirming, setConfirming] = useState(false);
  const [refusalKey, setRefusalKey] = useState<string | null>(null);

  // The emailed link opens a request at any of the person's accounts, so the
  // request is dated in the zone of the utility that owns its property (else its
  // device); the active membership stands in only when no membership names it.
  const orgId = facility?.org_id ?? installation?.org_id;
  const owning = context?.memberships.find((m) => m.org_id === orgId) ?? active;
  const timezone = owning?.org_timezone;
  const serial = present(request.device_serial);
  const title =
    requestDeviceLabel(installation) ??
    (serial ? t("deviceRow.serial", { serial }) : t("deviceRow.unnamed"));
  const propertyName = present(facility?.name) ?? undefined;
  const counterparty = request.counterparty;
  const tester = counterparty
    ? counterparty.kind === "tester"
      ? t("requestDetail.independent", { name: counterparty.name })
      : counterparty.name
    : null;
  const preferred = request.preferred_date
    ? formatLongInstantDay(request.preferred_date, timezone, i18n.language)
    : t("requestDetail.noPreference");
  const reportId = request.accepted_submission_id;
  const cancellable = canCancelRequest(request.status);

  function confirmCancel() {
    setRefusalKey(null);
    cancel.mutate(request.id, {
      onSuccess: () => setConfirming(false),
      onError: (error) => {
        // The work started, or a sibling cancelled it, between the render and
        // the click: the reason is shown and the reread corrects the status.
        setConfirming(false);
        setRefusalKey(cancelRequestErrorKey(refusalCode(error)));
        onReread();
      },
    });
  }

  return (
    <div className={PAGE}>
      <PageTitle
        title={title}
        sub={propertyName}
        back={{ label: t("nav.tabs.myRequests"), link: requestsLink() }}
        trail={[{ label: t("nav.tabs.myRequests"), link: requestsLink() }, { label: title }]}
      />
      <div className={GRID}>
        <section
          className={`rounded-card border border-line bg-surface p-4 shadow-card lg:p-5 ${
            request.thread ? "lg:col-start-2 lg:row-start-1" : ""
          }`}
        >
          <StatusChip
            tone={REQUEST_STATUS_TONE[request.status]}
            label={t(REQUEST_STATUS_LABEL_KEY[request.status])}
          />
          <dl className="mt-3.5 grid grid-cols-[auto_1fr] border-t border-line">
            <Row
              label={t("requestDetail.requested")}
              value={formatLongInstantDay(request.created_at, timezone, i18n.language)}
            />
            <Row label={t("requestDetail.preferred")} value={preferred} />
            <Row label={t("requestDetail.with")} value={tester} />
            <Row label={t("requestDetail.serial")} value={serial} tabular />
          </dl>
          {(reportId || cancellable) && (
            <div className="mt-4 flex flex-wrap gap-2.5">
              {reportId && (
                <Link {...submissionLink(reportId)} className={buttonClasses("primary")}>
                  {t("requestDetail.report")}
                </Link>
              )}
              {cancellable && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setRefusalKey(null);
                    setConfirming(true);
                  }}
                >
                  {t("myRequests.cancel.action")}
                </Button>
              )}
            </div>
          )}
          {refusalKey && (
            <p role="alert" className="mt-3 text-sm text-tone-red">
              {t(refusalKey)}
            </p>
          )}
        </section>
        {request.thread && (
          <div className="min-w-0 lg:col-start-1 lg:row-start-1">
            <ThreadPane threadId={request.thread.id} subject={propertyName} />
          </div>
        )}
      </div>
      <ConfirmDialog
        open={confirming}
        title={t("myRequests.cancel.title")}
        message={t("myRequests.cancel.message")}
        confirmLabel={t("myRequests.cancel.confirm")}
        cancelLabel={t("myRequests.cancel.keep")}
        confirmDisabled={cancel.isPending}
        onConfirm={confirmCancel}
        onCancel={() => setConfirming(false)}
      />
    </div>
  );
}

function Row({ label, value, tabular }: { label: string; value: ReactNode; tabular?: boolean }) {
  return (
    <>
      <dt className={TERM}>{label}</dt>
      <dd className={tabular ? `${VALUE} tracking-[0.02em] tabular-nums` : VALUE}>
        {value ?? <EmptyValue />}
      </dd>
    </>
  );
}
